import React, { useState, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './MarketsPage.module.css';
import { ThemeContext } from '../../context/ThemeContext';
import StocksPage from './StocksPage';
import FnOPage from './FnOPage';
import MutualFundPage from './MutualFundsPage';
import { FaRegNewspaper, FaChartLine, FaCubes, FaHandHoldingUsd } from 'react-icons/fa';
import * as api from '../../utils/api';
import { isMarketOpen } from '../../utils/marketTimeChecker';

const NewsSection = () => {
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchNews = async () => {
            try {
                const response = await api.getNews();
                if (response.data && response.data.length > 0) {
                    setNews(response.data.slice(0, 5));
                }
            } catch (error) {
                console.error("Error fetching news from backend:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchNews();
    }, []);

    if (loading) return <div className={styles.newsLoading}>Loading Market News...</div>;

    return (
        <div className={styles.newsSection}>
            <div className={styles.sectionHeader}>
                <FaRegNewspaper />
                <h3>Latest Market News</h3>
            </div>
            <div className={styles.newsList}>
                {news.length > 0 ? (
                    news.map((item, index) => (
                        <div key={index} className={styles.newsItem}>
                            <div className={styles.newsContent}>
                                <p className={styles.newsTitle}>{item.title}</p>
                                <div className={styles.newsMeta}>
                                    <span>{item.pubDate ? new Date(item.pubDate).toLocaleDateString() : 'Recent'}</span>
                                    <span>•</span>
                                    <span>{item.source || 'Economic Times'}</span>
                                </div>
                            </div>
                            {item.thumbnail && <img src={item.thumbnail} alt="news" className={styles.newsThumb} />}
                        </div>
                    ))
                ) : (
                    <div className={styles.noNews}>No recent market news available at the moment.</div>
                )}
            </div>
        </div>
    );
};

const MarketsPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { theme } = useContext(ThemeContext);
    
    // Determine active tab from URL query or default to stocks
    const query = new URLSearchParams(location.search);
    const initialTab = query.get('tab') || 'stocks';
    const [activeTab, setActiveTab] = useState(initialTab);
    
    const [isMarketOpenStatus, setIsMarketOpenStatus] = useState(false);

    // Update market status based on active tab
    useEffect(() => {
        const checkStatus = () => {
            const status = isMarketOpen('equity');
            setIsMarketOpenStatus(status.isOpen);
        };
        
        checkStatus();
        const interval = setInterval(checkStatus, 60000);
        return () => clearInterval(interval);
    }, [activeTab]);

    useEffect(() => {
        const tab = new URLSearchParams(location.search).get('tab') || 'stocks';
        setActiveTab(tab);
    }, [location.search]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        navigate(`/?tab=${tab}`, { replace: true });
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'fno':
                return <FnOPage />;
            case 'MF':
                return <MutualFundPage />;
            case 'stocks':
            default:
                return <StocksPage />;
        }
    };

    return (
        <div className={`${styles['markets-container']} ${theme}`}>
            <div className={styles['markets-header']}>
                <div className={styles['header-top']}>
                    <h1>Markets</h1>
                    <div className={`${styles['market-status']} ${!isMarketOpenStatus ? styles.closed : ''}`}>
                        <span className={`${styles['status-dot']} ${!isMarketOpenStatus ? styles.closed : ''}`}></span>
                        {isMarketOpenStatus ? 'Live Markets' : 'Markets Closed'}
                    </div>
                </div>
                
                <div className={styles['tabs-wrapper']}>
                    <div className={styles['tabs-container']}>
                        {[
                            { id: 'stocks', label: 'Stocks', icon: <FaChartLine /> },
                            { id: 'fno', label: 'F&O', icon: <FaCubes /> },
                            { id: 'MF', label: 'Mutual Fund', icon: <FaHandHoldingUsd /> }
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                className={`${styles['tab-btn']} ${activeTab === tab.id ? styles.active : ''}`}
                                onClick={() => handleTabChange(tab.id)}
                            >
                                <span className={styles.tabIcon}>{tab.icon}</span>
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className={styles['scrollable-content']}>
                <div className={styles['content-area']}>
                    {renderContent()}
                </div>
                
                {activeTab === 'stocks' && <NewsSection />}
            </div>
        </div>
    );
};

export default MarketsPage;
